import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    Inject,
    Input,
    OnDestroy,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService, LogService } from '@ridango/core';
import { NewMobileMenuAbstractComponent } from '../new-mobile-menus-abstract.component';
import { HelperService } from '../../../../services/helper.service';
import Env from '@ridango/core/env';
import { FeatureFlagService } from '@ridango/feature-flags';
import { MatDialog } from '@angular/material/dialog';
import { ApplicationFacade, CustomerAccountsFacade, CustomerFacade } from '@ridango/components';
import { MobileMenuVlComponent } from '../mobile-menu-vl/mobile-menu-vl.component';
import { NewMobileMenuComponent } from '../new-mobile-menu/new-mobile-menu.component';
import { merge, Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { HeaderMenuService } from '../../../../services/header-menu.service';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { Actions } from '@ngrx/effects';
import { MessagesService } from '../../../../../services/messages.service';
import { PermissionsFacade } from '@ridango/components/lib/permissions';

@Component({
    selector: 'gl-mobile-menu-wrapper',
    templateUrl: './new-mobile-menu-wrapper.component.html',
    styleUrls: ['./new-mobile-menu-wrapper.component.scss']
})
export class NewMobileMenuWrapperComponent extends NewMobileMenuAbstractComponent implements AfterViewInit, OnDestroy {
    @ViewChild('container', { read: ViewContainerRef }) container!: ViewContainerRef;
    @Input() trapFocus = true;
    private eventsEnum = {
        LANGUAGE: 'language',
        CONTRAST: 'contrast',
        CLOSE: 'close'
    };
    onDestroy = new Subject<void>();

    constructor(
        protected ls: LogService,
        public ridangoAuth: AuthService,
        public helperService: HelperService,
        public translate: TranslateService,
        @Inject('ENV') public env: Env,
        protected featureFlags: FeatureFlagService,
        private headerMenuService: HeaderMenuService,
        protected dialog: MatDialog,
        protected customerFacade: CustomerFacade,
        protected componentFactoryResolver: ComponentFactoryResolver,
        private cdr: ChangeDetectorRef,
        protected applicationFacade: ApplicationFacade,
        public analyticsService: AnalyticsService,
        public permissionsFacade: PermissionsFacade,
        public actions$: Actions,
        public messagesService: MessagesService,
        customerAccountsFacade: CustomerAccountsFacade
    ) {
        super(
            ls,
            ridangoAuth,
            helperService,
            translate,
            env,
            dialog,
            applicationFacade,
            customerFacade,
            permissionsFacade,
            actions$,
            messagesService,
            analyticsService,
            customerAccountsFacade
        );
    }

    ngAfterViewInit(): void {
        this.loadComponent();
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.complete();
    }

    private loadComponent(): void {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.getMenuComponent());
        this.container.clear();
        const componentRef = this.container.createComponent(componentFactory);
        this.headerMenuService.handleOpenMenu();

        this.tagEvent(componentRef)
            .pipe(
                takeUntil(this.onDestroy),
                tap(({ data, type }) => {
                    switch (type) {
                        case this.eventsEnum.LANGUAGE:
                            this.didLanguageChange.emit(data);
                            break;
                        case this.eventsEnum.CONTRAST:
                            this.hasContrastChanged = true;
                            this.themeToggle.emit();
                            break;
                        case this.eventsEnum.CLOSE:
                            this.didCloseMobileMenu.emit();
                            break;
                    }
                })
            )
            .subscribe();

        this.cdr.detectChanges();
    }

    private tagEvent(componentRef: ComponentRef<any>): Observable<any> {
        const events = [];

        if (componentRef.instance.didLanguageChange) {
            events.push(
                componentRef.instance.didLanguageChange.pipe(map((data) => ({ type: this.eventsEnum.LANGUAGE, data })))
            );
        }

        if (componentRef.instance.themeToggle) {
            events.push(
                componentRef.instance.themeToggle.pipe(map((data) => ({ type: this.eventsEnum.CONTRAST, data })))
            );
        }

        if (componentRef.instance.didCloseMobileMenu) {
            events.push(
                componentRef.instance.didCloseMobileMenu.pipe(map((data) => ({ type: this.eventsEnum.CLOSE, data })))
            );
        }

        return merge(...events);
    }

    private getMenuComponent(): Type<any> {
        switch (this.env.APP_NAME) {
            case 'vastmanland-app':
                return MobileMenuVlComponent;
            default:
                return NewMobileMenuComponent;
        }
    }
}
